import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Platform, ViewToken } from 'react-native';

import {
    CastPerson,
    MixedItem,
    RBChannel,
} from 'store/railsBuilder/reducer.types.ts';

import { ContentType, NowNextData } from 'services/data-manager/types';

import { isWeb } from 'utils/responsiveness';

import { ColumnsCollectionItemsList } from 'screens/Collection/types';

function useViewableItems(
    items: ColumnsCollectionItemsList,
    metadataType: ContentType | undefined,
    isCollection = false,
) {
    const defaultViewableItems = isCollection ? 20 : 5;
    // On Web `onViewableItemsChanged` is not called on the initial mount,
    // that's why we're automatically setting 20 first items for collection
    // and 5 first items for stripe as "isViewable" to trigger progress bar update.
    const visibleItemsRef = useRef(
        Platform.select({
            web: items
                .slice(0, defaultViewableItems)
                .reduce<Record<string, boolean>>((acc, item) => {
                    if (metadataType === ContentType.NowNext) {
                        return {
                            ...acc,
                            [createViewableNowNextId(item as NowNextData)]:
                                true,
                        };
                    }

                    if (metadataType === ContentType.ChannelItem) {
                        return {
                            ...acc,
                            [(item as RBChannel).editorial?.id]: true,
                        };
                    }

                    if (metadataType === ContentType.CastPerson) {
                        return { ...acc, [(item as CastPerson).name]: true };
                    }

                    return { ...acc, [(item as MixedItem)?.id]: true };
                }, {}),
            default: {},
        }),
    );

    const viewabilityConfig = useMemo(
        () => ({ itemVisiblePercentThreshold: 10, waitForInteraction: false }),
        [],
    );

    const handleViewableItemsChanged = useCallback(
        ({ changed }: { viewableItems: ViewToken[]; changed: ViewToken[] }) => {
            if (metadataType === ContentType.NowNext) {
                changed.forEach(({ item, isViewable }) => {
                    visibleItemsRef.current[createViewableNowNextId(item)] =
                        isViewable;
                });
            } else if (metadataType === ContentType.ChannelItem) {
                changed.forEach(({ item, isViewable }) => {
                    visibleItemsRef.current[item.editorial?.id] = isViewable;
                });
            } else if (metadataType === ContentType.CastPerson) {
                changed.forEach(({ item, isViewable }) => {
                    visibleItemsRef.current[item.name] = isViewable;
                });
            } else {
                changed.forEach(({ item, isViewable }) => {
                    visibleItemsRef.current[item?.id] = isViewable;
                });
            }
        },
        [metadataType],
    );

    const prevItemsLength = useRef(items.length);

    useEffect(() => {
        // On Web `onViewableItemsChanged` is not called on the initial mount,
        // that's why we're automatically setting 20 first items for collection
        // and 5 first items for stripe as "isViewable" to trigger progress bar update.
        if (isWeb) {
            if (!prevItemsLength.current && items.length) {
                handleViewableItemsChanged({
                    // @ts-ignore
                    changed: items
                        .slice(0, defaultViewableItems)
                        .map((item) => ({ item, isViewable: true })),
                });
            }

            prevItemsLength.current = items.length;
        }
    }, [items.length]);

    return {
        viewabilityConfig,
        visibleItems: visibleItemsRef.current,
        onViewableItemsChanged: handleViewableItemsChanged,
    };
}

export default useViewableItems;

export function createViewableNowNextId({ channelId }: NowNextData) {
    return `${channelId}-channel-now-next`;
}
